import React, { useState } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from './ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';

const FilterBar = ({ onFilterChange, activeFilters }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [filters, setFilters] = useState({
    tipo: '',
    tipoPropiedad: '',
    ciudad: '',
    precioMin: '',
    precioMax: '',
    habitacionesMin: ''
  });

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    onFilterChange(newFilters);
  };

  const clearFilters = () => {
    const emptyFilters = {
      tipo: '',
      tipoPropiedad: '',
      ciudad: '',
      precioMin: '',
      precioMax: '',
      habitacionesMin: ''
    };
    setFilters(emptyFilters);
    onFilterChange(emptyFilters);
  };

  const hasActiveFilters = Object.values(filters).some(value => value !== '');

  return (
    <div className="bg-white rounded-xl shadow-md p-6 mb-8">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Search className="text-blue-900" size={24} />
          <h3 className="text-lg font-bold text-gray-800">Buscar Propiedades</h3>
        </div>
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="flex items-center gap-2 text-amber-600 hover:text-amber-700 font-medium text-sm transition-colors"
        >
          <SlidersHorizontal size={18} />
          {showAdvanced ? 'Ocultar filtros' : 'Más filtros'}
        </button>
      </div>

      {/* Filtros básicos */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Operación</label>
          <select
            value={filters.tipo}
            onChange={(e) => handleFilterChange('tipo', e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none transition-all bg-white"
          >
            <option value="">Todos</option>
            <option value="venta">Venta</option>
            <option value="renta">Renta</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 font-body">Tipo de Propiedad</label>
          <Select value={filters.tipoPropiedad || 'todos'} onValueChange={(value) => handleFilterChange('tipoPropiedad', value === 'todos' ? '' : value)}>
            <SelectTrigger className="w-full font-body">
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              <SelectItem value="Casa">Casa</SelectItem>
              <SelectItem value="Departamento">Departamento</SelectItem>
              <SelectItem value="Terreno">Terreno</SelectItem>
              <SelectItem value="Local Comercial">Local Comercial</SelectItem>
              <SelectItem value="Oficina">Oficina</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 font-body">Ciudad</label>
          <Select value={filters.ciudad || 'todas'} onValueChange={(value) => handleFilterChange('ciudad', value === 'todas' ? '' : value)}>
            <SelectTrigger className="w-full font-body">
              <SelectValue placeholder="Todas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas</SelectItem>
              <SelectItem value="Playa del Carmen">Playa del Carmen</SelectItem>
              <SelectItem value="Cancún">Cancún</SelectItem>
              <SelectItem value="Tulum">Tulum</SelectItem>
              <SelectItem value="Cozumel">Cozumel</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Filtros avanzados */}
      {showAdvanced && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4 border-t border-gray-200 animate-in">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Precio Mínimo</label>
            <input
              type="number"
              value={filters.precioMin}
              onChange={(e) => handleFilterChange('precioMin', e.target.value)}
              placeholder="$0"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Precio Máximo</label>
            <input
              type="number"
              value={filters.precioMax}
              onChange={(e) => handleFilterChange('precioMax', e.target.value)}
              placeholder="Sin límite"
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-900 focus:border-transparent outline-none transition-all"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 font-body">Habitaciones Mínimas</label>
            <Select value={filters.habitacionesMin || 'cualquiera'} onValueChange={(value) => handleFilterChange('habitacionesMin', value === 'cualquiera' ? '' : value)}>
              <SelectTrigger className="w-full font-body">
                <SelectValue placeholder="Cualquiera" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cualquiera">Cualquiera</SelectItem>
                <SelectItem value="1">1+</SelectItem>
                <SelectItem value="2">2+</SelectItem>
                <SelectItem value="3">3+</SelectItem>
                <SelectItem value="4">4+</SelectItem>
                <SelectItem value="5">5+</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Botón limpiar filtros */}
      {hasActiveFilters && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <Button
            onClick={clearFilters}
            variant="outline"
            className="flex items-center gap-2"
          >
            <X size={16} />
            Limpiar Filtros
          </Button>
        </div>
      )}
    </div>
  );
};

export default FilterBar;
